'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';

const pains = [
  {
    title: 'The hiring clock and the board clock disagree',
    body: 'Recruiting, offer, notice period, ramp: a productive in-house SDR is two quarters away. Pipeline expectations start now.',
  },
  {
    title: 'Capital efficiency is being graded',
    body: 'Investors reward revenue per dollar spent. A dedicated outsourced rep at about a third of the cost reads very differently in your burn than a roughly $10,000 per month in-house seat.',
  },
  {
    title: 'You need signal before you scale headcount',
    body: 'Outbound proves the ICP and the message first. Hire your internal team into a motion that already works.',
  },
];

export default function FundedHero() {
  const rightRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    const els = rightRef?.current?.querySelectorAll('.rev');
    els?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <header
      className="py-24"
      style={{
        background:
          'radial-gradient(900px 500px at 85% -10%, rgba(201,137,65,0.15), transparent 60%), var(--navy-deep)',
      }}
    >
      <div className="max-w-site mx-auto px-7">
        <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] gap-16 items-start">
          <div>
            <div className="inline-flex font-mono text-[12px] tracking-[0.18em] uppercase text-primary border border-primary/50 px-4 py-2 rounded-full mb-6">
              For newly funded Series A and B teams
            </div>
            <h1 className="heading-land text-white">You raised. The board expects pipeline.</h1>
            <p className="mt-5 text-[19px] text-muted-cream leading-relaxed max-w-[56ch]">
              A sales hire takes months to find and months more to ramp. Your first board meeting is sooner than both. CommandVA deploys trained, dedicated reps in about 10 days, calling your market while a hiring process would still be screening resumes.
            </p>
            <div className="flex flex-wrap gap-3.5 mt-9">
              <a
                href="/request-meeting"
                className="inline-flex items-center gap-2.5 font-semibold text-sm px-6 py-3.5 rounded-[10px] bg-primary text-navy-deep border border-transparent transition-all duration-200 hover:bg-gold-bright hover:-translate-y-px hover:shadow-gold"
              >
                Book a strategy call
              </a>
              <Link
                href="/math"
                className="inline-flex items-center gap-2.5 font-semibold text-sm px-6 py-3.5 rounded-[10px] border border-line-navy text-white transition-all duration-200 hover:border-primary hover:text-primary"
              >
                See the math
              </Link>
            </div>
          </div>

          <div ref={rightRef} className="pt-4">
            {pains?.map((p, i) => (
              <div
                key={p?.title}
                className="rev pain-item"
                style={{ transitionDelay: `${i * 0.1}s` }}
              >
                <b className="block text-[16.5px] mb-1 text-white font-semibold">{p?.title}</b>
                <p className="text-[15px] text-muted-cream leading-relaxed">{p?.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
}